import { SurveyParticipant } from '../../types/survey'
import { createApiClient } from './apiClient'

// Function to create API client for participant routes (no auth required)
const createParticipantApiClient = () => {
  return createApiClient()
}

interface SubmitSurveyRequest {
  surveyId: string
  surveyParticipantId: string
  grade: string
  surveyStartedAt: number
  surveySubmittedAt: number
  surveyName: string
  indicators: Array<{
    indicator: string
    contentId: string
    indicatorId?: string
    tags?: string[]
    responses: Array<{
      questionId: string
      questionText: string
      responseOptionField: string
      responseScore: number
      questionResponseOptionsType: string
      inputRule?: string
    }>
  }>
  createdAt: string
  demographicQuestions?: Array<{
    questionText: string
    responseOptionField: string
    responseScore: string | number
  }>
  validityQuestions?: Array<{
    questionId: string
    validityIndex?: string
    responseOptionField: string
    responseScore: number
  }>
  isAllCore: boolean
  orgId: string
  dataSent?: boolean
  parentSurveyAuthCode?: string
  wellnessCenterCheck?: boolean
  firstName?: string
  lastName?: string
  participantEmail?: string
  schoolName?: string
  birthDate?: number
  appliedTo?: string
}

// Participant Survey API Service
export const participantSurveyService = {
  // Authenticate participant with access code
  authenticateParticipant: async (accessCode: string): Promise<SurveyParticipant> => {
    const apiClient = createParticipantApiClient()
    try {
      console.log('Making API call to /participant/verify with:', accessCode)
      const response = await apiClient.post('/participant/verify', {
        inviteCode: accessCode
      })
      console.log('API call successful:', response.data)
      return response.data
    } catch (error: unknown) {
      console.log('[participantSurveyService][authenticateParticipant] Error:', error)
      console.log('Service error caught:', error)
      
      // Type guard for error with response property
      const errorWithResponse = error as { response?: { status?: number; data?: { message?: string } }; request?: unknown };
      
      console.log('Error response status:', errorWithResponse.response?.status)
      console.log('Error response data:', errorWithResponse.response?.data)
      // Handle non-200 responses
      if (errorWithResponse.response) {
        // Check if there's a message in the response payload
        const responseData = errorWithResponse.response.data
        if (responseData && responseData.message) {
          console.log('Using message from response payload:', responseData.message)
          throw new Error(responseData.message)
        } else {
          // Default to invalid invite code if no message provided
          console.log('Throwing Invalid invite code. error')
          throw new Error('Invalid invite code.')
        }
      } else if (errorWithResponse.request) {
        // Network error
        throw new Error('Network error. Please check your connection and try again.')
      } else {
        // Other error
        throw new Error('An unexpected error occurred. Please try again.')
      }
    }
  },

  // Submit completed survey
  submitSurvey: async (surveyData: SubmitSurveyRequest): Promise<void> => {
    const apiClient = createParticipantApiClient()
    try {
      await apiClient.post('/participant/save', surveyData)
    } catch (error: unknown) {
      console.log('[participantSurveyService][submitSurvey] Error:', error)
      
      // Type guard for error with response property
      const errorWithResponse = error as { response?: { data?: { message?: string } }; request?: unknown };
      
      if (errorWithResponse.response) {
        // Check if there's a message in the response payload
        const responseData = errorWithResponse.response.data
        if (responseData && responseData.message) {
          throw new Error(responseData.message)
        } else {
          throw new Error('Failed to save survey responses. Please try again.')
        }
      } else if (errorWithResponse.request) {
        throw new Error('Network error. Please check your connection and try again.')
      } else {
        throw new Error('An unexpected error occurred while saving your responses.')
      }
    }
  },

  // Verify participant info (if needed)
  verifyParticipant: async (verificationData: {
    inviteCode: string
    firstName: string
    lastName: string
    email: string
  }): Promise<{ message: string; status: number }> => {
    const apiClient = createParticipantApiClient()
    try {
      const response = await apiClient.post('/participant/verify', verificationData)
      return response.data
    } catch (error: unknown) {
      console.log('[participantSurveyService][verifyParticipant] Error:', error)
      
      // Type guard for error with response property
      const errorWithResponse = error as { response?: { status?: number; data?: { message?: string } }; request?: unknown };
      
      if (errorWithResponse.response) {
        // Check if there's a message in the response payload
        const responseData = errorWithResponse.response.data
        if (responseData && responseData.message) {
          throw new Error(responseData.message)
        } else {
          // Fallback to status-based error messages
          if (errorWithResponse.response.status === 400 || errorWithResponse.response.status === 404) {
            throw new Error('Invalid participant information.')
          } else {
            throw new Error('An error occurred while verifying participant information.')
          }
        }
      } else if (errorWithResponse.request) {
        throw new Error('Network error. Please check your connection and try again.')
      } else {
        throw new Error('An unexpected error occurred.')
      }
    }
  },

  // Get survey data (if needed for additional data)
  getSurveyData: async (surveyId: string): Promise<unknown> => {
    const apiClient = createParticipantApiClient()
    try {
      const response = await apiClient.get(`/survey/${surveyId}/data`)
      return response.data
    } catch (error: unknown) {
      console.log('[participantSurveyService][getSurveyData] Error:', error)
      
      // Type guard for error with response property
      const errorWithResponse = error as { response?: { data?: { message?: string } }; request?: unknown };
      
      if (errorWithResponse.response) {
        // Check if there's a message in the response payload
        const responseData = errorWithResponse.response.data
        if (responseData && responseData.message) {
          throw new Error(responseData.message)
        } else {
          throw new Error('Failed to load survey data.')
        }
      } else if (errorWithResponse.request) {
        throw new Error('Network error. Please check your connection and try again.')
      } else {
        throw new Error('An unexpected error occurred while loading survey data.')
      }
    }
  }
} 